/**
 * AccessDisabledScreen
 *
 * Shown when a user's membership has been disabled (active=false).
 * Must NEVER show "permission denied" — this is the deterministic disabled UX.
 *
 * R12: App gating checks membership.active before attempting team reads,
 * so this screen is shown before any team-scoped providers mount.
 */

import { LogOut, Mail, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

interface AccessDisabledScreenProps {
  teamName?: string;
  ownerEmail?: string;
}

export function AccessDisabledScreen({ teamName, ownerEmail }: AccessDisabledScreenProps) {
  const { t } = useTranslation('common');
  const { logout } = useAuth();

  const handleSignOut = async () => {
    try {
      await logout();
    } catch {
      window.location.href = '/login';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center space-y-6 max-w-md w-full">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <ShieldOff className="w-8 h-8 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-xl font-semibold">
            {t('accessDisabled.title', 'Toegang uitgeschakeld')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {teamName
              ? t('accessDisabled.descriptionWithTeam', {
                  teamName,
                  defaultValue: `Je toegang tot ${teamName} is uitgeschakeld door een beheerder.`,
                })
              : t(
                  'accessDisabled.description',
                  'Je toegang tot dit team is uitgeschakeld door een beheerder.'
                )}
          </p>
          <p className="text-sm text-muted-foreground">
            {t(
              'accessDisabled.dataNote',
              'Je gegevens blijven bewaard. Neem contact op met de teameigenaar voor reactivatie.'
            )}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {ownerEmail && (
            <Button variant="outline" asChild>
              <a href={`mailto:${ownerEmail}?subject=Toegang reactiveren`}>
                <Mail className="w-4 h-4 mr-2" />
                {t('accessDisabled.contactOwner', 'Contact teameigenaar')}
              </a>
            </Button>
          )}

          <Button variant="outline" asChild>
            <a href="mailto:support@modcar.io?subject=Toegang uitgeschakeld">
              <Mail className="w-4 h-4 mr-2" />
              {t('accessDisabled.contactSupport', 'Contact support')}
            </a>
          </Button>

          <Button variant="ghost" onClick={handleSignOut}>
            <LogOut className="w-4 h-4 mr-2" />
            {t('accessDisabled.signOut', 'Uitloggen')}
          </Button>
        </div>
      </div>
    </div>
  );
}
